// src/services/curriculumEditorService.js
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
} from 'firebase/firestore';
import { db } from './firebase.js';

const TOPICS_COLLECTION = 'curriculum_topics';
const QUESTIONS_COLLECTION = 'curriculum_questions';
const API_BASE_URL = 'https://smartbrain-theta.vercel.app';

// Curated syllabus outlines for Nigerian secondary school curriculum
export const SYLLABUS_TEMPLATES = {
  mathematics: {
    ss1: [
      { id: 'ss1_math_number_bases', title: 'Number Base System' },
      { id: 'ss1_math_modular_arithmetic', title: 'Modular Arithmetic' },
      { id: 'ss1_math_indices', title: 'Indices' },
      { id: 'ss1_math_logarithms', title: 'Logarithms' },
      { id: 'ss1_math_sets', title: 'Sets & Venn Diagrams' },
    ],
    ss2: [
      { id: 'ss2_math_quadratic_equations', title: 'Quadratic Equations & Factorization' },
      { id: 'ss2_math_simultaneous_equations', title: 'Simultaneous Linear & Quadratic Equations' },
      { id: 'ss2_math_arithmetic_progression', title: 'Arithmetic Progression (A.P)' },
      { id: 'ss2_math_geometric_progression', title: 'Geometric Progression (G.P)' },
      { id: 'ss2_math_trigonometry', title: 'Trigonometric Ratios & Elevation' },
    ],
    ss3: [
      { id: 'ss3_math_matrices_determinants', title: 'Matrices & Determinants' },
      { id: 'ss3_math_differentiation', title: 'Calculus: Differentiation & Tangents' },
      { id: 'ss3_math_integration', title: 'Calculus: Integration & Areas' },
      { id: 'ss3_math_coordinate_geometry', title: 'Coordinate Geometry & Straight Lines' },
    ],
    jss1: [
      { id: 'jss1_math_whole_numbers', title: 'Whole Numbers & Place Values' },
      { id: 'jss1_math_lcm_hcf', title: 'LCM and HCF of Numbers' },
      { id: 'jss1_math_fractions', title: 'Fractions & Percentages' },
      { id: 'jss1_math_basic_algebra', title: 'Simple Algebraic Expressions' },
    ],
    jss2: [
      { id: 'jss2_math_algebraic_expressions', title: 'Algebraic Expressions & Factorization' },
      { id: 'jss2_math_simple_equations', title: 'Linear Equations in One Variable' },
      { id: 'jss2_math_angles_polygons', title: 'Angles & Properties of Polygons' },
      { id: 'jss2_math_statistics_data', title: 'Data Presentation: Bar Charts & Frequency' },
    ],
    jss3: [
      { id: 'jss3_math_simultaneous_equations', title: 'Simultaneous Linear Equations' },
      { id: 'jss3_math_trigonometry_angles', title: 'Basic Trigonometry & Right-Angled Triangles' },
      { id: 'jss3_math_plane_geometry', title: 'Circle Theorems & Plane Shapes' },
      { id: 'jss3_math_bece_prep', title: 'BECE / Junior WAEC Exam Revision' },
    ],
  },
  physics: {
    ss1: [
      { id: 'ss1_phy_scalar_vectors', title: 'Scalars, Vectors & Physical Quantities' },
      { id: 'ss1_phy_motion', title: 'Motion: Speed, Velocity & Acceleration' },
      { id: 'ss1_phy_gravitational_field', title: 'Gravitational Field & Free Fall' },
      { id: 'ss1_phy_work_energy_power', title: 'Work, Energy and Power' },
    ],
    ss2: [
      { id: 'ss2_phy_heat_temperature', title: 'Heat Energy & Thermal Expansion' },
      { id: 'ss2_phy_waves', title: 'Production and Propagation of Waves' },
      { id: 'ss2_phy_reflection_light', title: 'Reflection of Light on Mirrors' },
      { id: 'ss2_phy_electric_field', title: 'Electric Field & Coulomb\'s Law' },
    ],
    ss3: [
      { id: 'ss3_phy_electromagnetic_induction', title: 'Electromagnetic Induction & Transformers' },
      { id: 'ss3_phy_quantum_physics', title: 'Photoelectric Effect & Photons' },
      { id: 'ss3_phy_radioactivity', title: 'Radioactivity & Nuclear Reactions' },
    ],
  },
  chemistry: {
    ss1: [
      { id: 'ss1_chem_particulate_nature', title: 'Particulate Nature of Matter & Atoms' },
      { id: 'ss1_chem_electronic_config', title: 'Electronic Configuration & Periodic Table' },
      { id: 'ss1_chem_chemical_bonding', title: 'Chemical Bonding: Covalent & Electrovalent' },
      { id: 'ss1_chem_acids_bases', title: 'Acids, Bases and Salts' },
    ],
    ss2: [
      { id: 'ss2_chem_gas_laws', title: "Gas Laws: Boyle's, Charles's & Ideal Gas Law" },
      { id: 'ss2_chem_oxidation_reduction', title: 'Redox Reactions & Oxidation Numbers' },
      { id: 'ss2_chem_electrolysis', title: "Electrolysis & Faraday's Laws" },
      { id: 'ss2_chem_hydrocarbons', title: 'Alkanes, Alkenes & Alkynes' },
    ],
    ss3: [
      { id: 'ss3_chem_chemical_equilibrium', title: 'Chemical Equilibrium & Le Chatelier' },
      { id: 'ss3_chem_organic_reactions', title: 'Esters, Polymers & Biomolecules' },
      { id: 'ss3_chem_metals_extraction', title: 'Extraction & Purification of Metals' },
    ],
  },
  english: {
    ss1: [
      { id: 'ss1_eng_parts_of_speech', title: 'Parts of Speech: Nouns and Pronouns' },
      { id: 'ss1_eng_verbs_tenses', title: 'Verbs, Tenses & Subject-Verb Agreement' },
      { id: 'ss1_eng_lexis_structure', title: 'Vocabulary: Health, Agriculture & Transport' },
      { id: 'ss1_eng_essay_writing', title: 'Narrative & Descriptive Essay Writing' },
    ],
    ss2: [
      { id: 'ss2_eng_concord', title: 'Advanced Grammatical Concord & Tenses' },
      { id: 'ss2_eng_idioms_figures', title: 'Idiomatic Expressions & Figures of Speech' },
      { id: 'ss2_eng_formal_letter', title: 'Formal Letter & Article Writing' },
      { id: 'ss2_eng_comprehension_summary', title: 'Reading Comprehension & Summary Skills' },
    ],
  },
  biology: {
    ss1: [
      { id: 'ss1_bio_cell_structure', title: 'The Cell as a Living Unit & Organelles' },
      { id: 'ss1_bio_classification', title: 'Classification of Living Organisms' },
      { id: 'ss1_bio_nutrition', title: 'Nutrition in Plants & Animals' },
      { id: 'ss1_bio_ecosystem', title: 'Basic Ecological Concepts & Food Chains' },
    ],
    ss2: [
      { id: 'ss2_bio_transport_system', title: 'Transport Systems in Plants & Animals' },
      { id: 'ss2_bio_respiration', title: 'Aerobic & Anaerobic Cellular Respiration' },
      { id: 'ss2_bio_excretory_system', title: 'Excretory Systems & Kidney Function' },
      { id: 'ss2_bio_reproduction', title: 'Reproduction in Flowering Plants' },
    ],
  },
  economics: {
    ss1: [
      { id: 'ss1_econ_basic_concepts', title: 'Basic Concepts: Scarcity, Choice & Scale of Preference' },
      { id: 'ss1_econ_production', title: 'Factors of Production & Division of Labour' },
      { id: 'ss1_econ_demand_supply', title: 'Theory of Demand and Supply' },
    ],
    ss2: [
      { id: 'ss2_econ_elasticity', title: 'Price Elasticity of Demand and Supply' },
      { id: 'ss2_econ_market_structures', title: 'Market Structures: Perfect Competition & Monopoly' },
      { id: 'ss2_econ_money_inflation', title: 'Money, Banking and Inflation' },
    ],
  },
  further_mathematics: {
    ss1: [
      { id: 'ss1_fmath_indices_surds', title: 'Indices & Surds' },
      { id: 'ss1_fmath_logarithms', title: 'Logarithms & Exponential Equations' },
      { id: 'ss1_fmath_polynomials', title: 'Polynomials & Remainder Theorem' },
      { id: 'ss1_fmath_functions', title: 'Functions & Mappings' },
      { id: 'ss1_fmath_coordinate_geometry', title: 'Coordinate Geometry: Straight Lines' },
    ],
    ss2: [
      { id: 'ss2_fmath_inequalities', title: 'Quadratic & Rational Inequalities' },
      { id: 'ss2_fmath_trigonometry', title: 'Compound & Double Angle Trigonometry' },
      { id: 'ss2_fmath_differentiation', title: 'Differentiation from First Principles & Rules' },
      { id: 'ss2_fmath_integration', title: 'Integration: Indefinite and Definite Integrals' },
      { id: 'ss2_fmath_permutations', title: 'Permutations and Combinations' },
    ],
    ss3: [
      { id: 'ss3_fmath_matrices', title: 'Matrices & Linear Transformations' },
      { id: 'ss3_fmath_vectors', title: 'Vectors in Two and Three Dimensions' },
      { id: 'ss3_fmath_probability', title: 'Probability Distributions & Binomial Theorem' },
      { id: 'ss3_fmath_statics_dynamics', title: 'Mechanics: Statics, Dynamics and Projectiles' },
    ],
  },
};

/**
 * Generate default structured lesson steps for a topic (used ONLY upon explicit generation)
 */
export const generateDefaultSteps = (topicTitle, classId = 'ss1', subjectId = 'subject') => {
  const cName = classId.toUpperCase();
  const sName = subjectId.charAt(0).toUpperCase() + subjectId.slice(1);
  return [
    {
      stepNumber: 1,
      title: `Introduction to ${topicTitle}`,
      concept: `In ${cName} ${sName}, mastering ${topicTitle} is foundational. We examine core definitions, standard terminology, and syllabus expectations for WAEC and JAMB.`,
      analogy: `Think of this like laying the foundation of a building: clear understanding of terms makes answering complex exam questions straightforward.`,
      keyRule: `Memorize and apply the exact standard definition and units of ${topicTitle}.`,
      example: `Identify standard variables and state foundational principles with clear reasoning.`,
      checkQuestion: {
        text: `Which statement best captures the main focus of ${topicTitle}?`,
        options: [
          `Understanding foundational concepts and standard syllabus rules`,
          `Skipping intermediate reasoning to guess`,
          `Using arbitrary and unrelated units`,
          `Ignoring standard secondary school conventions`,
        ],
        correctAnswer: `Understanding foundational concepts and standard syllabus rules`,
        explanation: `Under WAEC and NECO standards, examiners require direct adherence to verified definitions.`,
      },
    },
    {
      stepNumber: 2,
      title: `Core Rules & Practical Applications in ${topicTitle}`,
      concept: `Now we look at procedural problem-solving in ${topicTitle}, applying formulas, structured rules, and sequential analysis.`,
      analogy: `Just as cooking a Nigerian delicacy requires following procedural steps, solving problems in this topic requires following ordered steps.`,
      keyRule: `Always double-check intermediate units, signs, and arithmetic before stating the final answer.`,
      example: `Worked Example: Step 1 write the formula; Step 2 substitute standard values; Step 3 evaluate and attach units.`,
      checkQuestion: {
        text: `What is the most reliable approach when solving problems in ${topicTitle}?`,
        options: [
          `Follow sequential step-by-step procedure and verify units`,
          `Jump straight to an unverified guess`,
          `Omit standard units from final answers`,
          `Disregard question conditions`,
        ],
        correctAnswer: `Follow sequential step-by-step procedure and verify units`,
        explanation: `Method marks in WAEC and JAMB are awarded for step-by-step logical working.`,
      },
    },
    {
      stepNumber: 3,
      title: `WAEC & JAMB Exam Mastery in ${topicTitle}`,
      concept: `In this step, we review past examination style problems to build exam confidence, accuracy, and speed for tests and certification exams.`,
      analogy: `Like an athlete undergoing focused practice before the championship match, solving past exam questions sharpens your speed and accuracy.`,
      keyRule: `Read questions carefully, check for trick distractors, and verify solutions.`,
      example: `Examination-level breakdown: Identify the objective, eliminate incorrect options, and confirm with syllabus rule.`,
      checkQuestion: {
        text: `How can secondary school students guarantee full marks in ${topicTitle}?`,
        options: [
          `Show structured steps, verify calculations, and state proper units`,
          `Leave questions blank or rush without review`,
          `Write answers without showing formulas or logic`,
          `Ignore time management`,
        ],
        correctAnswer: `Show structured steps, verify calculations, and state proper units`,
        explanation: `Examiners reward clean presentation, correct formulas, and proper unit conversions.`,
      },
    },
  ];
};

// Helper: Flexible class level matching (e.g., 'ss2', 'SS 2', 'SS2', 'Senior Secondary 2')
export const isClassMatch = (itemClass, targetClass) => {
  if (!itemClass || !targetClass) return false;
  const cleanItem = String(itemClass).toLowerCase().replace(/[^a-z0-9]/g, '');
  const cleanTarget = String(targetClass).toLowerCase().replace(/[^a-z0-9]/g, '');
  if (cleanItem === cleanTarget) return true;
  if (cleanTarget.startsWith('ss') && cleanItem === `seniorsecondary${cleanTarget.slice(2)}`) return true;
  if (cleanTarget.startsWith('jss') && cleanItem === `juniorsecondary${cleanTarget.slice(3)}`) return true;
  if (cleanTarget.startsWith('seniorsecondary') && cleanItem === `ss${cleanTarget.slice(15)}`) return true;
  if (cleanTarget.startsWith('juniorsecondary') && cleanItem === `jss${cleanTarget.slice(15)}`) return true;
  return false;
};

// Helper: Normalize subject to standard canonical identifier (matching mobile app logic)
export const normalizeSubjectId = (raw) => {
  if (!raw) return '';
  const s = String(raw).toLowerCase().trim()
    .replace(/_(ss1|ss2|ss3|jss1|jss2|jss3|utme)$/i, '')
    .replace(/[^a-z0-9]/g, '');

  // 1. Further Mathematics MUST be evaluated before General Mathematics
  if (
    s === 'furthermaths' ||
    s === 'furthermathematics' ||
    s === 'furthermath' ||
    s === 'fmaths' ||
    s.includes('furthermath') ||
    s.includes('fmath')
  ) {
    return 'further_mathematics';
  }

  // 2. General Mathematics
  if (
    s === 'mathematics' ||
    s === 'maths' ||
    s === 'math' ||
    s === 'generalmathematics' ||
    s.includes('generalmath')
  ) {
    return 'mathematics';
  }

  // 3. Other core subjects
  if (s === 'eng' || s === 'english' || s === 'englishlanguage' || s.includes('english')) return 'english';
  if (s === 'phy' || s === 'physic' || s === 'physics') return 'physics';
  if (s === 'chem' || s === 'chemistry') return 'chemistry';
  if (s === 'bio' || s === 'biology') return 'biology';
  if (s === 'econ' || s === 'economics') return 'economics';
  if (s === 'govt' || s === 'government') return 'government';
  if (s === 'lit' || s === 'literature' || s.includes('literature')) return 'literature';
  if (s === 'agric' || s.includes('agriculture') || s.includes('agricultural')) return 'agric';
  if (s === 'civic' || s.includes('civic')) return 'civic';
  if (s === 'ict' || s.includes('computer')) return 'ict';
  if (s.includes('basictech')) return 'basic_tech';
  if (s.includes('basicsci') || s.includes('integratedsci')) return 'basic_science';
  if (s.includes('account')) return 'accounting';
  if (s.includes('market')) return 'marketing';
  if (s.includes('social')) return 'social_studies';
  if (s.includes('business')) return 'business_studies';

  return s;
};

// Helper: Flexible subject matching (strictly separates Further Maths from General Maths)
export const isSubjectMatch = (itemSubject, targetSubject) => {
  if (!itemSubject || !targetSubject) return false;
  const normItem = normalizeSubjectId(itemSubject);
  const normTarget = normalizeSubjectId(targetSubject);

  if (normItem && normTarget) {
    return normItem === normTarget;
  }

  return String(itemSubject).toLowerCase().trim() === String(targetSubject).toLowerCase().trim();
};

/**
 * Fetch all topics for a class and subject
 * Searches:
 * 1. 'curriculum_topics' collection
 * 2. 'content_approvals' collection (teacher/admin approved topics)
 * 3. 'syllabus' collection (both standalone topics and subject topic arrays)
 * 4. Automatic fallback to official NERDC SYLLABUS_TEMPLATES so it never displays an empty 0
 */
export const fetchCurriculumTopics = async (classId = 'ss1', subjectId = 'mathematics') => {
  const topics = [];
  const normalizedClass = (classId || 'ss1').toLowerCase().trim();
  const upperClass = (classId || 'SS1').toUpperCase().trim();
  const normalizedSubject = normalizeSubjectId(subjectId || 'mathematics');

  // 0. Load deleted topics blacklist from localStorage and Firestore
  const deletedBlacklist = [];
  try {
    const lsKey = `deleted_topics_${normalizedClass}_${normalizedSubject}`;
    const stored = JSON.parse(localStorage.getItem(lsKey) || '[]');
    if (Array.isArray(stored)) {
      deletedBlacklist.push(...stored);
    }
  } catch (_) {}

  try {
    const snapDeleted = await getDocs(collection(db, 'curriculum_deleted_topics'));
    snapDeleted.docs.forEach((d) => {
      const data = d.data();
      const dClass = (data.classId || '').toLowerCase().trim();
      const dSubj = (data.subjectId || '').toLowerCase().trim();
      if ((!dClass || isClassMatch(dClass, normalizedClass)) && (!dSubj || isSubjectMatch(dSubj, normalizedSubject))) {
        deletedBlacklist.push({
          cleanTitle: data.cleanTitle || (data.title || '').toLowerCase().trim(),
          topicId: data.topicId,
        });
      }
    });
  } catch (err) {
    console.warn('curriculum_deleted_topics read error:', err?.message);
  }

  const isTopicDeleted = (tId, tTitle) => {
    const cleanT = (tTitle || '').toLowerCase().trim();
    return deletedBlacklist.some((d) => {
      if (d.cleanTitle && cleanT && d.cleanTitle === cleanT) return true;
      if (d.topicId && tId && (d.topicId === tId)) return true;
      return false;
    });
  };

  // 1. Check curriculum_topics collection
  try {
    const snap = await getDocs(collection(db, TOPICS_COLLECTION));
    snap.docs.forEach((d) => {
      const data = d.data();
      const dClass = data.classId || data.class || '';
      const dSubj = data.subjectId || data.subject || '';
      if (isClassMatch(dClass, normalizedClass) && isSubjectMatch(dSubj, normalizedSubject)) {
        const topicTitle = data.title || data.name || '';
        const topicId = data.topicId || d.id;
        if (topicTitle && !isTopicDeleted(topicId, topicTitle) && !topics.some((t) => (t.title || '').toLowerCase() === topicTitle.toLowerCase())) {
          topics.push({
            id: d.id,
            topicId,
            title: topicTitle,
            classId: normalizedClass,
            subjectId: normalizedSubject,
            term: data.term || 'Term 1',
            order: data.order !== undefined && data.order !== null ? Number(data.order) : topics.length + 1,
            stepsCount: data.stepsCount || (data.steps ? data.steps.length : 3),
            steps: data.steps && data.steps.length > 0 ? data.steps : generateDefaultSteps(topicTitle, normalizedClass, normalizedSubject),
            source: 'curriculum_topics',
            docId: d.id,
          });
        }
      }
    });
  } catch (err) {
    console.warn('curriculum_topics collection read error:', err?.message);
  }

  // 2. Check content_approvals collection for teacher/admin created topics & lesson topics
  try {
    const snapApprovals = await getDocs(collection(db, 'content_approvals'));
    snapApprovals.docs.forEach((d) => {
      const data = d.data();
      // Class can be in classLevel, class, or extracted from subjectId (e.g., 'english_ss2' -> 'ss2')
      const derivedClass = data.classLevel || data.class || (data.subjectId ? data.subjectId.split('_').pop() : '');
      const itemSubj = data.subjectId || data.subjectName || data.subject || '';

      if (isClassMatch(derivedClass, normalizedClass) && isSubjectMatch(itemSubj, normalizedSubject)) {
        // Case A: Standalone Topic document
        if (data.type === 'Topic' || data.type === 'topic') {
          const topicTitle = (data.title || data.name || '').trim();
          const topicId = d.id;
          if (topicTitle && !isTopicDeleted(topicId, topicTitle) && !topics.some((t) => (t.title || '').toLowerCase().trim() === topicTitle.toLowerCase())) {
            topics.push({
              id: d.id,
              topicId,
              title: topicTitle,
              classId: normalizedClass,
              subjectId: normalizedSubject,
              term: data.term || 'Term 1',
              order: data.order !== undefined ? data.order : topics.length + 1,
              stepsCount: data.stepsCount || (data.steps ? data.steps.length : 3),
              steps: data.steps && data.steps.length > 0 ? data.steps : generateDefaultSteps(topicTitle, normalizedClass, normalizedSubject),
              status: data.status || 'approved',
              source: 'content_approvals',
              docId: d.id,
            });
          }
        } else {
          // Case B: Lesson or Content document with an associated topic (e.g., "Modular Arithmetic")
          const topicTitle = (
            data.topicName ||
            data.topicTitle ||
            data.contentData?.topicName ||
            data.contentData?.topicTitle ||
            ''
          ).trim();

          const topicId = data.topicId || `topic_${topicTitle.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;

          if (topicTitle && !isTopicDeleted(topicId, topicTitle) && !topics.some((t) => (t.title || '').toLowerCase().trim() === topicTitle.toLowerCase())) {
            topics.push({
              id: topicId,
              topicId,
              title: topicTitle,
              classId: normalizedClass,
              subjectId: normalizedSubject,
              term: data.term || data.contentData?.term || 'Term 1',
              order: data.order !== undefined ? data.order : topics.length + 1,
              stepsCount: 3,
              steps: generateDefaultSteps(topicTitle, normalizedClass, normalizedSubject),
              status: data.status || 'approved',
              source: 'content_approvals_lesson',
              docId: d.id,
            });
          }
        }
      }
    });
  } catch (err) {
    console.warn('content_approvals read error:', err?.message);
  }

  // 3. Check syllabus collection (handles both topic docs and subject docs with topics arrays)
  try {
    const snapSyllabus = await getDocs(collection(db, 'syllabus'));
    snapSyllabus.docs.forEach((d) => {
      const data = d.data();
      const docClass = data.class || data.classLevel || '';
      const docSubj = data.subject || data.subjectId || data.name || '';

      if (isClassMatch(docClass, normalizedClass) && isSubjectMatch(docSubj, normalizedSubject)) {
        // Case A: Standalone topic doc
        if (data.type === 'topic' || (!data.type && !Array.isArray(data.topics))) {
          const topicTitle = data.name || data.title || '';
          const topicId = d.id;
          if (topicTitle && !isTopicDeleted(topicId, topicTitle) && !topics.some((t) => (t.title || '').toLowerCase() === topicTitle.toLowerCase())) {
            topics.push({
              id: d.id,
              topicId,
              title: topicTitle,
              classId: normalizedClass,
              subjectId: normalizedSubject,
              term: data.term || 'Term 1',
              order: data.order !== undefined ? data.order : topics.length + 1,
              stepsCount: data.stepsCount || (data.steps ? data.steps.length : 3),
              steps: data.steps && data.steps.length > 0 ? data.steps : generateDefaultSteps(topicTitle, normalizedClass, normalizedSubject),
              source: 'syllabus_collection',
              docId: d.id,
            });
          }
        }

        // Case B: Subject doc containing topics array
        if (Array.isArray(data.topics)) {
          data.topics.forEach((subTopic, idx) => {
            const topicTitle = typeof subTopic === 'string' ? subTopic : subTopic?.title || subTopic?.name || '';
            const topicId = typeof subTopic === 'object' && subTopic?.id ? subTopic.id : `${d.id}_t_${idx}`;
            if (topicTitle && !isTopicDeleted(topicId, topicTitle) && !topics.some((t) => (t.title || '').toLowerCase() === topicTitle.toLowerCase())) {
              topics.push({
                id: topicId,
                topicId,
                title: topicTitle,
                classId: normalizedClass,
                subjectId: normalizedSubject,
                term: (typeof subTopic === 'object' && subTopic?.term) ? subTopic.term : 'Term 1',
                order: (typeof subTopic === 'object' && subTopic?.order !== undefined) ? subTopic.order : idx + 1,
                stepsCount: 3,
                steps: (typeof subTopic === 'object' && subTopic?.steps) ? subTopic.steps : generateDefaultSteps(topicTitle, normalizedClass, normalizedSubject),
                source: 'syllabus_collection',
                docId: d.id,
                isSubTopicArray: true,
                subTopicIndex: idx,
              });
            }
          });
        }
      }
    });
  } catch (err) {
    console.warn('syllabus collection read error:', err?.message);
  }

  // 4. Check local session storage (if explicitly created/generated during this active session)
  const sessionKey = `curriculum_${normalizedClass}_${normalizedSubject}`;
  try {
    const cached = sessionStorage.getItem(sessionKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        parsed.forEach((cachedTopic) => {
          if (cachedTopic?.source !== 'nerdc_syllabus_template') {
            const topicTitle = cachedTopic.title || cachedTopic.name || '';
            const topicId = cachedTopic.topicId || cachedTopic.id;
            if (topicTitle && !isTopicDeleted(topicId, topicTitle) && !topics.some((t) => (t.title || '').toLowerCase() === topicTitle.toLowerCase())) {
              topics.push(cachedTopic);
            }
          }
        });
      }
    }
  } catch (_) {}

  return topics.sort((a, b) => (a.order || 0) - (b.order || 0));
};

/**
 * Fetch questions for a class and subject
 * Searches strictly:
 * 1. 'curriculum_questions' collection
 * 2. 'content_approvals' collection (quizzes created in TeacherQuizBuilder with type 'Quiz')
 * 3. 'quizzes' collection
 * (Only returns real database items or explicitly generated AI questions, no fake fallbacks)
 */
export const fetchCurriculumQuestions = async (classId = 'ss1', subjectId = 'mathematics', topicId = null) => {
  const questions = [];
  const normalizedClass = (classId || 'ss1').toLowerCase().trim();
  const normalizedSubject = normalizeSubjectId(subjectId || 'mathematics');

  // 1. Check curriculum_questions collection
  try {
    const snap = await getDocs(collection(db, QUESTIONS_COLLECTION));
    snap.docs.forEach((d) => {
      const data = d.data();
      const dClass = data.classId || data.class || '';
      const dSubj = data.subjectId || data.subject || '';
      if (isClassMatch(dClass, normalizedClass) && isSubjectMatch(dSubj, normalizedSubject)) {
        if (!topicId || data.topicId === topicId) {
          questions.push({
            id: d.id,
            ...data,
          });
        }
      }
    });
  } catch (err) {
    console.warn('curriculum_questions read error:', err?.message);
  }

  // 2. Check content_approvals collection for quizzes (TeacherQuizBuilder)
  try {
    const qQuizzes = query(
      collection(db, 'content_approvals'),
      where('type', '==', 'Quiz')
    );
    const snapQuizzes = await getDocs(qQuizzes);
    snapQuizzes.docs.forEach((d) => {
      const data = d.data();
      const derivedClass = data.classLevel || data.class || (data.subjectId ? data.subjectId.split('_').pop() : '');
      const itemSubj = data.subjectId || data.subjectName || data.subject || '';

      if (isClassMatch(derivedClass, normalizedClass) && isSubjectMatch(itemSubj, normalizedSubject)) {
        const rawQuestions = data.contentData?.questions || data.questions || [];
        if (Array.isArray(rawQuestions)) {
          rawQuestions.forEach((qItem, idx) => {
            const qText = qItem.questionText || qItem.question || qItem.text || '';
            if (qText) {
              const opts = qItem.options || [];
              let correctAns = qItem.correctAnswer || qItem.answer || '';
              if (!correctAns && typeof qItem.correctAnswerIndex === 'number' && opts[qItem.correctAnswerIndex]) {
                correctAns = opts[qItem.correctAnswerIndex];
              }

              questions.push({
                id: `${d.id}_q_${idx}`,
                topicId: data.topicId || topicId || d.id,
                topicTitle: data.title || data.topicTitle || data.topicName || '',
                classId: normalizedClass,
                subjectId: normalizedSubject,
                question: qText,
                options: opts,
                correctAnswer: correctAns,
                explanation: qItem.explanation || `WAEC exam question from quiz: ${data.title || 'Teacher Quiz'}`,
                difficulty: qItem.difficulty || 2,
                approved: data.status === 'approved',
                status: data.status || 'approved',
                source: data.title || 'Teacher Quiz Builder',
              });
            }
          });
        }
      }
    });
  } catch (err) {
    console.warn('content_approvals quizzes read error:', err?.message);
  }

  // 3. Check legacy quizzes collection
  try {
    const snapLegacyQuizzes = await getDocs(collection(db, 'quizzes'));
    snapLegacyQuizzes.docs.forEach((d) => {
      const data = d.data();
      const dClass = data.class || data.classId || '';
      const dSubj = data.subject || data.subjectId || '';
      if (isClassMatch(dClass, normalizedClass) && isSubjectMatch(dSubj, normalizedSubject)) {
        if (Array.isArray(data.questions)) {
          data.questions.forEach((qItem, idx) => {
            const qText = qItem.question || qItem.questionText || qItem.text || '';
            if (qText) {
              questions.push({
                id: qItem.id || `${d.id}_${idx}`,
                topicId: qItem.topicId || topicId || d.id,
                topicTitle: qItem.topicTitle || qItem.topicName || data.title || '',
                classId: normalizedClass,
                subjectId: normalizedSubject,
                question: qText,
                options: qItem.options || [],
                correctAnswer: qItem.correctAnswer || qItem.answer || '',
                explanation: qItem.explanation || 'WAEC standard practice question',
                approved: true,
                status: 'approved',
                source: data.title || 'Quiz Bank',
              });
            }
          });
        }
      }
    });
  } catch (_) {}

  // 4. Check session storage
  const sessionKey = `questions_${normalizedClass}_${normalizedSubject}`;
  try {
    const cached = sessionStorage.getItem(sessionKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) {
        parsed.forEach((qItem) => {
          if (!questions.some((q) => q.id === qItem.id || (q.question && q.question === qItem.question))) {
            questions.push(qItem);
          }
        });
      }
    }
  } catch (_) {}

  if (topicId) {
    return questions.filter((q) => q.topicId === topicId);
  }

  return questions;
};

/**
 * Save or update a curriculum topic (including its lesson steps)
 */
/**
 * Save or update a curriculum topic (including its lesson steps)
 */
export const saveCurriculumTopic = async (topicData) => {
  const topicId = topicData.topicId || topicData.id || `topic_${Date.now()}`;
  const orderNum = topicData.order !== undefined && topicData.order !== null ? Number(topicData.order) : 1;
  const payload = {
    ...topicData,
    topicId,
    id: topicId,
    order: orderNum,
    updatedAt: new Date().toISOString(),
  };

  const normalizedClass = (topicData.classId || 'ss1').toLowerCase().trim();
  const normalizedSubject = normalizeSubjectId(topicData.subjectId || 'mathematics');
  const cleanTitle = (topicData.title || '').toLowerCase().trim();

  try {
    const docRef = doc(db, TOPICS_COLLECTION, topicId);
    await setDoc(docRef, payload, { merge: true });
  } catch (err) {
    console.warn('Direct Firestore save to curriculum_topics failed:', err?.message);
  }

  // Also sync to source document if topic was originally from content_approvals or syllabus
  if (topicData.docId) {
    if (topicData.source === 'content_approvals' || topicData.source === 'content_approvals_lesson') {
      try {
        const approvalRef = doc(db, 'content_approvals', topicData.docId);
        await setDoc(
          approvalRef,
          {
            order: orderNum,
            term: payload.term,
            title: payload.title,
            topicTitle: payload.title,
            topicName: payload.title,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        );
      } catch (e) {
        console.warn('Failed to sync to content_approvals source:', e?.message);
      }
    } else if (topicData.source === 'syllabus_collection') {
      try {
        const sylRef = doc(db, 'syllabus', topicData.docId);
        await setDoc(
          sylRef,
          {
            order: orderNum,
            term: payload.term,
            title: payload.title,
            name: payload.title,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        );
      } catch (e) {
        console.warn('Failed to sync to syllabus source:', e?.message);
      }
    }
  }

  // If this topic was previously in the deleted blacklist, unblacklist it now
  try {
    const lsKey = `deleted_topics_${normalizedClass}_${normalizedSubject}`;
    const stored = JSON.parse(localStorage.getItem(lsKey) || '[]');
    const filtered = stored.filter((item) => item.cleanTitle !== cleanTitle && item.topicId !== topicId);
    localStorage.setItem(lsKey, JSON.stringify(filtered));

    const deletedDocId = `${normalizedClass}_${normalizedSubject}_${cleanTitle.replace(/[^a-z0-9]/g, '_')}`;
    await deleteDoc(doc(db, 'curriculum_deleted_topics', deletedDocId)).catch(() => {});
  } catch (_) {}

  // Update session storage
  const sessionKey = `curriculum_${normalizedClass}_${normalizedSubject}`;
  try {
    const current = JSON.parse(sessionStorage.getItem(sessionKey) || '[]');
    const idx = current.findIndex((t) => (t.topicId || t.id) === topicId);
    if (idx >= 0) {
      current[idx] = payload;
    } else {
      current.push(payload);
    }
    sessionStorage.setItem(sessionKey, JSON.stringify(current));
  } catch (_) {}

  // Invalidate backend AI teacher cache for this topic and class/subject list
  try {
    fetch(`${API_BASE_URL}/api/ai-teacher`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'clear_cache', cacheType: 'topic', topicId }),
    }).catch(() => {});
  } catch (_) {}

  return payload;
};

/**
 * Delete a curriculum topic across all database stores:
 * 1. curriculum_topics collection
 * 2. content_approvals collection
 * 3. syllabus collection (standalone docs AND topics array inside subject documents)
 * 4. curriculum_deleted_topics (persistent blacklist tombstone)
 * 5. localStorage & sessionStorage caches
 */
export const deleteCurriculumTopic = async (topicOrId, options = {}) => {
  const isObj = typeof topicOrId === 'object' && topicOrId !== null;
  const topicId = isObj ? (topicOrId.topicId || topicOrId.id) : topicOrId;
  const topicTitle = isObj ? (topicOrId.title || topicOrId.name) : options.title;
  const classId = (isObj ? (topicOrId.classId || options.classId) : options.classId) || 'ss1';
  const subjectId = (isObj ? (topicOrId.subjectId || options.subjectId) : options.subjectId) || 'mathematics';
  const docId = isObj ? (topicOrId.docId || topicOrId.id) : options.docId;
  const isSubTopicArray = isObj ? topicOrId.isSubTopicArray : options.isSubTopicArray;

  const normalizedClass = (classId || 'ss1').toLowerCase().trim();
  const normalizedSubject = normalizeSubjectId(subjectId || 'mathematics');
  const cleanTitle = (topicTitle || '').toLowerCase().trim();

  console.log(`[deleteCurriculumTopic] Deleting topic:`, {
    topicId,
    topicTitle,
    classId: normalizedClass,
    subjectId: normalizedSubject,
    docId,
    isSubTopicArray,
  });

  // 1. Delete from curriculum_topics collection
  try {
    if (topicId) {
      await deleteDoc(doc(db, TOPICS_COLLECTION, topicId)).catch(() => {});
    }
    if (docId && docId !== topicId) {
      await deleteDoc(doc(db, TOPICS_COLLECTION, docId)).catch(() => {});
    }

    const snapCurriculum = await getDocs(collection(db, TOPICS_COLLECTION)).catch(() => null);
    if (snapCurriculum && snapCurriculum.docs) {
      for (const d of snapCurriculum.docs) {
        const data = d.data();
        const dTitle = (data.title || data.name || '').toLowerCase().trim();
        const dTopicId = data.topicId || d.id;
        const dClass = (data.classId || data.class || '').toLowerCase().trim();
        const dSubj = (data.subjectId || data.subject || '').toLowerCase().trim();

        if (
          dTopicId === topicId ||
          d.id === docId ||
          (cleanTitle && dTitle === cleanTitle && isClassMatch(dClass, normalizedClass) && isSubjectMatch(dSubj, normalizedSubject))
        ) {
          await deleteDoc(doc(db, TOPICS_COLLECTION, d.id)).catch(() => {});
        }
      }
    }
  } catch (err) {
    console.warn('curriculum_topics delete handler error:', err?.message);
  }

  // 2. Delete from content_approvals collection
  try {
    if (docId) {
      await deleteDoc(doc(db, 'content_approvals', docId)).catch(() => {});
    }
    if (topicId && topicId !== docId) {
      await deleteDoc(doc(db, 'content_approvals', topicId)).catch(() => {});
    }

    const qApprovals = query(
      collection(db, 'content_approvals'),
      where('type', '==', 'Topic')
    );
    const snapApprovals = await getDocs(qApprovals).catch(() => null);
    if (snapApprovals && snapApprovals.docs) {
      for (const d of snapApprovals.docs) {
        const data = d.data();
        const dTitle = (data.title || data.name || '').toLowerCase().trim();
        const dSubj = (data.subjectId || data.subjectName || data.subject || '').toLowerCase().trim();
        if (
          d.id === topicId ||
          d.id === docId ||
          (cleanTitle && dTitle === cleanTitle && isSubjectMatch(dSubj, normalizedSubject))
        ) {
          await deleteDoc(doc(db, 'content_approvals', d.id)).catch(() => {});
        }
      }
    }
  } catch (err) {
    console.warn('content_approvals delete handler error:', err?.message);
  }

  // 3. Delete from syllabus collection (standalone docs + subject topics array)
  try {
    if (docId && !isSubTopicArray) {
      await deleteDoc(doc(db, 'syllabus', docId)).catch(() => {});
    }
    if (topicId && !isSubTopicArray) {
      await deleteDoc(doc(db, 'syllabus', topicId)).catch(() => {});
    }

    const snapSyllabus = await getDocs(collection(db, 'syllabus')).catch(() => null);
    if (snapSyllabus && snapSyllabus.docs) {
      for (const d of snapSyllabus.docs) {
        const data = d.data();
        const docClass = (data.class || data.classLevel || '').toLowerCase().trim();
        const docSubj = (data.subject || data.subjectId || data.name || '').toLowerCase().trim();

        if (isClassMatch(docClass, normalizedClass) && isSubjectMatch(docSubj, normalizedSubject)) {
          // Case A: Standalone topic doc
          if (data.type === 'topic' || (!data.type && !Array.isArray(data.topics))) {
            const dTitle = (data.name || data.title || '').toLowerCase().trim();
            if (d.id === topicId || d.id === docId || (cleanTitle && dTitle === cleanTitle)) {
              await deleteDoc(doc(db, 'syllabus', d.id)).catch(() => {});
            }
          }

          // Case B: Subject doc containing topics array
          if (Array.isArray(data.topics)) {
            const prevLen = data.topics.length;
            const updatedTopics = data.topics.filter((subTopic, idx) => {
              const subTitle = (typeof subTopic === 'string' ? subTopic : subTopic?.title || subTopic?.name || '').toLowerCase().trim();
              const subId = typeof subTopic === 'object' && subTopic?.id ? subTopic.id : `${d.id}_t_${idx}`;
              if (topicId && (subId === topicId || (typeof subTopic === 'object' && subTopic?.id === topicId))) {
                return false;
              }
              if (cleanTitle && subTitle === cleanTitle) {
                return false;
              }
              return true;
            });

            if (updatedTopics.length !== prevLen) {
              await updateDoc(doc(db, 'syllabus', d.id), {
                topics: updatedTopics,
                updatedAt: new Date().toISOString(),
              }).catch((e) => console.warn('syllabus array updateDoc error:', e));
            }
          }
        }
      }
    }
  } catch (err) {
    console.warn('syllabus delete handler error:', err?.message);
  }

  // 4. Save persistent tombstone in Firestore curriculum_deleted_topics
  const deletedDocId = `${normalizedClass}_${normalizedSubject}_${cleanTitle.replace(/[^a-z0-9]/g, '_')}`;
  try {
    await setDoc(doc(db, 'curriculum_deleted_topics', deletedDocId), {
      classId: normalizedClass,
      subjectId: normalizedSubject,
      title: topicTitle || '',
      cleanTitle,
      topicId: topicId || '',
      deletedAt: new Date().toISOString(),
    });
  } catch (err) {
    console.warn('curriculum_deleted_topics setDoc error:', err?.message);
  }

  // 5. Save tombstone to localStorage
  try {
    const lsKey = `deleted_topics_${normalizedClass}_${normalizedSubject}`;
    const stored = JSON.parse(localStorage.getItem(lsKey) || '[]');
    if (!stored.some((item) => (item.cleanTitle && item.cleanTitle === cleanTitle) || (item.topicId && item.topicId === topicId))) {
      stored.push({ topicId, cleanTitle, title: topicTitle });
      localStorage.setItem(lsKey, JSON.stringify(stored));
    }
  } catch (_) {}

  // 6. Remove from session storage
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('curriculum_')) {
        const items = JSON.parse(sessionStorage.getItem(key) || '[]');
        const filtered = items.filter((t) => {
          const tId = t.topicId || t.id;
          const tTitle = (t.title || t.name || '').toLowerCase().trim();
          if (topicId && tId === topicId) return false;
          if (cleanTitle && tTitle === cleanTitle) return false;
          return true;
        });
        sessionStorage.setItem(key, JSON.stringify(filtered));
      }
    }
  } catch (_) {}

  // 7. Invalidate backend AI teacher cache
  try {
    fetch(`${API_BASE_URL}/api/ai-teacher`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'clear_cache', cacheType: 'topic', topicId }),
    }).catch(() => {});
  } catch (_) {}

  return true;
};

/**
 * Save or update a curriculum question
 */
export const saveCurriculumQuestion = async (qData) => {
  const qId = qData.id || `q_${Date.now()}`;
  const payload = {
    ...qData,
    id: qId,
    updatedAt: new Date().toISOString(),
  };

  try {
    const docRef = doc(db, QUESTIONS_COLLECTION, qId);
    await setDoc(docRef, payload, { merge: true });
  } catch (err) {
    console.warn('Direct Firestore question save failed (permissions):', err?.message);
  }

  // Update session storage
  const sessionKey = `questions_${qData.classId}_${qData.subjectId}`;
  try {
    const current = JSON.parse(sessionStorage.getItem(sessionKey) || '[]');
    const idx = current.findIndex((q) => q.id === qId);
    if (idx >= 0) {
      current[idx] = payload;
    } else {
      current.push(payload);
    }
    sessionStorage.setItem(sessionKey, JSON.stringify(current));
  } catch (_) {}

  // Invalidate backend AI teacher questions cache
  try {
    fetch(`${API_BASE_URL}/api/ai-teacher`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'clear_cache', cacheType: 'topic', topicId: qData.topicId }),
    }).catch(() => {});
  } catch (_) {}

  return payload;
};

/**
 * Delete a curriculum question
 */
export const deleteCurriculumQuestion = async (qId) => {
  try {
    await deleteDoc(doc(db, QUESTIONS_COLLECTION, qId));
  } catch (err) {
    console.warn('Direct Firestore question delete failed (permissions):', err?.message);
  }

  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('questions_')) {
        const items = JSON.parse(sessionStorage.getItem(key) || '[]');
        const filtered = items.filter((q) => q.id !== qId);
        sessionStorage.setItem(key, JSON.stringify(filtered));
      }
    }
  } catch (_) {}

  return true;
};

/**
 * Approve or toggle approval for a question
 */
export const setQuestionApproval = async (qId, isApproved) => {
  try {
    const docRef = doc(db, QUESTIONS_COLLECTION, qId);
    await updateDoc(docRef, {
      approved: isApproved,
      status: isApproved ? 'approved' : 'pending',
      approvedAt: isApproved ? new Date().toISOString() : null,
    });
  } catch (err) {
    console.warn('Direct question approval update failed (permissions):', err?.message);
  }
  return true;
};

/**
 * Dynamically seed or generate curriculum and questions based on selected class & subject
 * ONLY called when the user EXPLICITLY clicks the "Auto-Generate" button.
 */
export const triggerDynamicSeed = async ({ classId, subjectId, className, subjectName }) => {
  // 1. Call the backend API route with server-side admin privileges
  try {
    const endpoint =
      classId === 'ss1' && subjectId === 'mathematics'
        ? `${API_BASE_URL}/api/seed-ai-math`
        : `${API_BASE_URL}/api/seed-curriculum`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ classId, subjectId, className, subjectName }),
    });

    if (response.ok) {
      const data = await response.json();
      const generatedTopics = data.topics || [];
      if (generatedTopics.length > 0) {
        sessionStorage.setItem(`curriculum_${classId}_${subjectId}`, JSON.stringify(generatedTopics));
      }
      return {
        success: true,
        message: data.message || `Successfully generated curriculum for ${className} ${subjectName}!`,
        topicsSeeded: data.topicsSeeded || generatedTopics.length,
        questionsSeeded: data.questionsSeeded || 12,
        topics: generatedTopics,
      };
    }
  } catch (apiErr) {
    console.warn('Backend seed endpoint call failed, using client generator:', apiErr);
  }

  // 2. Client-side generator fallback (only triggers upon clicking button)
  const templateTopics =
    SYLLABUS_TEMPLATES[subjectId]?.[classId] || [
      { id: `${classId}_${subjectId}_intro_concepts`, title: `Fundamentals of ${subjectName} in ${className}` },
      { id: `${classId}_${subjectId}_core_theories`, title: `Core Principles & Applications in ${subjectName}` },
      { id: `${classId}_${subjectId}_problem_solving`, title: `Analytical Methods & Practice in ${subjectName}` },
      { id: `${classId}_${subjectId}_exam_prep`, title: `WAEC & Exam Preparation in ${subjectName}` },
    ];

  const topicsList = [];
  const questionsList = [];

  for (let i = 0; i < templateTopics.length; i++) {
    const t = templateTopics[i];
    const steps = generateDefaultSteps(t.title, classId, subjectId);
    const topicObj = {
      topicId: t.id,
      id: t.id,
      classId,
      subjectId,
      title: t.title,
      term: 'Term 1',
      order: i + 1,
      stepsCount: steps.length,
      steps,
    };
    await saveCurriculumTopic(topicObj);
    topicsList.push(topicObj);

    // Generate 3 exam practice questions per topic
    for (let qIdx = 1; qIdx <= 3; qIdx++) {
      const qObj = {
        id: `q_${t.id}_${qIdx}`,
        topicId: t.id,
        classId,
        subjectId,
        difficulty: qIdx,
        question: `[${className} ${subjectName} - ${t.title}] Question ${qIdx}: Which of the following is correct according to the standard syllabus?`,
        options: [
          `Principle ${qIdx} applies directly under normal conditions`,
          `The principle is inverted arbitrarily`,
          `No logical relation exists in this topic`,
          `The outcome cannot be determined`,
        ],
        correctAnswer: `Principle ${qIdx} applies directly under normal conditions`,
        explanation: `Under the approved Nigerian curriculum for ${className} ${subjectName}, Principle ${qIdx} is the established guideline.`,
        source: `SmartBrain ${className} ${subjectName} Bank`,
        approved: true,
        status: 'approved',
      };
      await saveCurriculumQuestion(qObj);
      questionsList.push(qObj);
    }
  }

  sessionStorage.setItem(`curriculum_${classId}_${subjectId}`, JSON.stringify(topicsList));
  sessionStorage.setItem(`questions_${classId}_${subjectId}`, JSON.stringify(questionsList));

  return {
    success: true,
    message: `Generated ${topicsList.length} topics and ${questionsList.length} practice questions for ${className} ${subjectName}.`,
    topicsSeeded: topicsList.length,
    questionsSeeded: questionsList.length,
    topics: topicsList,
  };
};

export const triggerSeedSS1Math = triggerDynamicSeed;

// --- DYNAMIC CUSTOM SUBJECT MANAGEMENT ---
const SUBJECTS_COLLECTION = 'curriculum_subjects';

export const fetchCustomSubjects = async () => {
  const customSubjects = [];
  try {
    const snap = await getDocs(collection(db, SUBJECTS_COLLECTION));
    snap.docs.forEach((d) => {
      customSubjects.push({ id: d.id, ...d.data() });
    });
  } catch (err) {
    console.warn('Error fetching custom subjects from Firestore:', err?.message);
  }

  // Merge with localStorage for resilience & offline preview
  try {
    const local = JSON.parse(localStorage.getItem('custom_curriculum_subjects') || '[]');
    if (Array.isArray(local)) {
      local.forEach((ls) => {
        if (!customSubjects.some((cs) => cs.id === ls.id)) {
          customSubjects.push(ls);
        }
      });
    }
  } catch (_) {}

  return customSubjects;
};

export const saveCustomSubject = async (subjectData) => {
  if (!subjectData || !subjectData.name) {
    throw new Error('Subject name is required');
  }
  const id = (subjectData.id || subjectData.name)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .trim();

  const payload = {
    ...subjectData,
    id,
    updatedAt: new Date().toISOString(),
  };

  try {
    await setDoc(doc(db, SUBJECTS_COLLECTION, id), payload, { merge: true });
  } catch (err) {
    console.warn('Error saving custom subject to Firestore, saved to local cache:', err?.message);
  }

  try {
    const local = JSON.parse(localStorage.getItem('custom_curriculum_subjects') || '[]');
    const filtered = local.filter((s) => s.id !== id);
    filtered.push(payload);
    localStorage.setItem('custom_curriculum_subjects', JSON.stringify(filtered));
  } catch (_) {}

  return payload;
};

export const fetchDeletedSubjectIds = async () => {
  let list = [];
  try {
    list = JSON.parse(localStorage.getItem('deleted_curriculum_subjects') || '[]');
  } catch (_) {}

  try {
    const snap = await getDoc(doc(db, 'curriculum_settings', 'hidden_subjects'));
    if (snap && snap.exists() && Array.isArray(snap.data()?.ids)) {
      snap.data().ids.forEach((id) => {
        if (!list.includes(id)) list.push(id);
      });
    }
  } catch (err) {
    console.warn('Error fetching hidden subjects:', err?.message);
  }

  return list;
};

export const deleteCustomSubject = async (subjectId) => {
  try {
    await deleteDoc(doc(db, SUBJECTS_COLLECTION, subjectId));
  } catch (err) {
    console.warn('Error deleting custom subject from Firestore:', err?.message);
  }

  try {
    const local = JSON.parse(localStorage.getItem('custom_curriculum_subjects') || '[]');
    const filtered = local.filter((s) => s.id !== subjectId);
    localStorage.setItem('custom_curriculum_subjects', JSON.stringify(filtered));
  } catch (_) {}

  // Record in hidden/deleted subjects list so any subject (including default) can be deleted
  try {
    let localDeleted = [];
    try {
      localDeleted = JSON.parse(localStorage.getItem('deleted_curriculum_subjects') || '[]');
    } catch (_) {}
    if (!localDeleted.includes(subjectId)) {
      localDeleted.push(subjectId);
      localStorage.setItem('deleted_curriculum_subjects', JSON.stringify(localDeleted));
    }
    await setDoc(
      doc(db, 'curriculum_settings', 'hidden_subjects'),
      {
        ids: localDeleted,
        updatedAt: new Date().toISOString(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn('Error recording deleted subject in Firestore settings:', err?.message);
  }

  return true;
};
